import identity from 'lodash/identity';
import React, { useEffect, useState } from 'react';
import FormControlError from '../FormControlError';
import uniqueId from 'lodash/uniqueId';

import './RadioField.less';

/**
 * @typedef TRadioOptionItem
 * @type {{ value:null|string; label?:null|string; ariaLabel?:string; disabled?:boolean; }}
 * @param {{
 *  ariaLabel?:string;
 *  ariaDescribedBy?:string;
 *  id?:string;
 *  title?:string;
 *  name?:string;
 *  required?:boolean;
 *  onChange?(name:string, v:null|string, o:null|TRadioOptionItem):void;
 *  selectedOptionValue?:null|string;
 *  options:TRadioOptionItem[];
 *  customErrorPrefix?:string|null;
 *  classNameWrapper?:string;
 *  horizontal?:boolean;
 *  postControlSlot?:((o:null|TRadioOptionItem) => React.ReactNode) | React.ReactNode;
 * }} props
 * @returns {React.ReactElement}
 */
export const RadioField = (props) => {
  const {
    title,
    name,
    horizontal,
    options,
    ariaLabel,
    ariaDescribedBy,
    required,
    selectedOptionValue,
    onChange,
    classNameWrapper,
    customErrorPrefix,
    postControlSlot
  } = props;

  const [isDirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState((/** @type {React.ReactNode} */(null)));
  const [selectedOption, setSelectedOption] = useState(/** @type {null|TRadioOptionItem} */(options.find(o => o.value === selectedOptionValue) ?? null));
  const classNames = [
    'lsn-control-radios',
    errorMessage ? 'has-error' : '',
    horizontal ? 'horizontal' : '',
    classNameWrapper,
  ].filter(identity).join(' ');
  const uiLibClassNames = ['form-radio'];
  const classNameControl = [
    ...uiLibClassNames,
    "lsn-control lsn-control-radio",
    errorMessage ? 'has-error' : '',
  ].filter(identity).join(' ');
  const [controlUID] = useState(props.id || uniqueId(`lsn_ctrl_`));

  /**
   *
   * @param {TRadioOptionItem} option
   */
  const handleChange = (option) => {
    if (option.disabled) { return; }
    setDirty(true);
    setSelectedOption(option);
    onChange?.(name ?? '', option.value, option);
  }

  /**
   *
   * @returns {string}
   */
  const _getNativeErrorMessage = () => {
    if (required && !selectedOption) {
      return `${customErrorPrefix ?? title ?? 'Entry'} is required`;
    }
    return '';
  };

  const refreshErrorMessage = () => {
    const errorMessage = isDirty ? _getNativeErrorMessage() : '';
    setErrorMessage(errorMessage);
  };

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleFocus = (e) => {
    setDirty(true);
  }

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleBlur = (e) => {
    setDirty(true);
  }

  /**
   *
   * @param {TRadioOptionItem} option
   */
  const renderOptionLabel = option => {
    return (option.label ?? option.value ?? '')?.toString() ?? '';
  };

  // set selectedOption when selectedOptionValue changed
  useEffect(() => {
    const selectedOption = options.find(o => o.value === selectedOptionValue);
    setSelectedOption(selectedOption ?? null);
    setDirty(typeof selectedOption === 'object' || isDirty);
  }, [selectedOptionValue]);

  // validate when isDirty or selectedOption changed
  useEffect(() => {
    refreshErrorMessage();
  }, [isDirty, selectedOption]);

  return (
    <>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-errormessage={`${controlUID}_error_msg`}
        aria-invalid={!!errorMessage}
        data-value={selectedOption?.value ?? ''}
        className={classNames}>
        {/* the form-radio className must be there. (https://picturepan2.github.io/spectre/elements/forms.html) */}
        {options.map((option, key) => (
          <label className={classNameControl} key={option.value}>
            <input
              aria-hidden

              id={`${controlUID}_${key}`}
              type="radio"
              tabIndex={-1}
              name={name}
              title={[(title ?? ''), (option.ariaLabel ?? option.label ?? '')].filter(identity).join(' ')}
              value={option.value ?? ''}
              checked={selectedOption?.value === option.value}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onInvalid={handleBlur}
              onChange={() => handleChange(option)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChange(option);
                }
              }}
              required={required}
            />
            {/* the form-icon className must be there. (https://picturepan2.github.io/spectre/elements/forms.html) */}
            <i
              role={'radio'}
              aria-label={option.ariaLabel ?? void 0}
              aria-labelledby={option.ariaLabel ? void 0 : `${controlUID}_${key}_label`}
              aria-checked={selectedOption?.value === option.value}

              tabIndex={option.disabled ? -1 : 0}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.code === 'Space') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChange(option);
                }
              }}
              className={'form-icon'} />
            <span className='suffix' dangerouslySetInnerHTML={{ __html: renderOptionLabel(option) }} id={`${controlUID}_${key}_label`}></span>
          </label>
        ))}
      </div>
      {required ? <div className='control-error-wrapper'>
        <FormControlError id={`${controlUID}_error_msg`} show={!!errorMessage}>{errorMessage}</FormControlError>
      </div> : null}
      {typeof postControlSlot === 'function' ? postControlSlot(selectedOption) : postControlSlot}
    </>
  );
}

export default RadioField;
